const ALLOWED_HOSTS = ['localhost', 'tinymgr-qa-dps.kone.com', 'tinymgr-dps.kone.com'];

const _rawApiUrl = process.env.REACT_APP_KEC_API || 'http://localhost:5002/api/kec';

// Validate API_BASE_URL against allowlist to prevent SSRF
const _validateUrl = (url) => {
  try {
    const parsed = new URL(url);
    if (!ALLOWED_HOSTS.includes(parsed.hostname)) {
      throw new Error(`API host "${parsed.hostname}" is not in the allowed list`);
    }
    return url;
  } catch (e) {
    console.error('[KECService] Invalid API URL:', e.message);
    return 'http://localhost:5002/api/kec';
  }
};

const API_BASE_URL = _validateUrl(_rawApiUrl);

const authHeader = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`,
});

/** Parse error body from a failed response */
const parseError = async (response, fallbackMsg) => {
  try {
    const body = await response.json();
    return body.error || body.message || fallbackMsg;
  } catch {
    return fallbackMsg;
  }
};

export const KECService = {

  /** Step 1 — Exchange client credentials for a grant token */
  getGrantToken: async (clientId, clientSecret, credType = 'private', secretName = '', region = 'global') => {
    const body = credType === 'public'
      ? { credType, region, ...(secretName && { secretName }) }
      : { credType, clientId, clientSecret, region };
    const response = await fetch(`${API_BASE_URL}/grant-token`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await parseError(response, `Grant token failed (${response.status})`));
    return response.json();
  },

  /** Step 2 — List buildings the application has access to */
  getResources: async (grantToken, region = 'global') => {
    const response = await fetch(`${API_BASE_URL}/resources`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ grantToken, region }),
    });
    if (!response.ok) throw new Error(await parseError(response, `Get resources failed (${response.status})`));
    return response.json();
  },

  /** Step 2b — Device Mapping: KEN → Building ID */
  deviceMapping: async (grantToken, kenId, region = 'global') => {
    const response = await fetch(`${API_BASE_URL}/device-mapping`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ grantToken, kenId, region }),
    });
    if (!response.ok) throw new Error(await parseError(response, `Device mapping failed (${response.status})`));
    return response.json();
  },

  /** Step 3 — Exchange for a building-scoped token */
  getScopeToken: async (clientId, clientSecret, buildingId, groupId = '1', credType = 'private', secretName = '', region = 'global') => {
    const body = credType === 'public'
      ? { credType, buildingId, groupId, region, ...(secretName && { secretName }) }
      : { credType, clientId, clientSecret, buildingId, groupId, region };
    const response = await fetch(`${API_BASE_URL}/scope-token`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await parseError(response, `Scope token failed (${response.status})`));
    return response.json();
  },

  /** Step 4 — Get building topology (floors, lifts, terminals) */
  getConfig: async (scopeToken, buildingId, groupId = '1', grantToken = '', ken = '', region = 'global') => {
    const body = { scopeToken, grantToken, buildingId, groupId, region };
    if (ken) body.ken = ken;
    const response = await fetch(`${API_BASE_URL}/config`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await parseError(response, `Get config failed (${response.status})`));
    return response.json();
  },

  /** Step 4b — Get supported call actions for a building/group */
  getActions: async (scopeToken, buildingId, groupId = '1', region = 'global') => {
    const response = await fetch(`${API_BASE_URL}/actions`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ scopeToken, buildingId, groupId, region }),
    });
    if (!response.ok) throw new Error(await parseError(response, `Get actions failed (${response.status})`));
    return response.json();
  },

  /** Ping elevator to check HTTP + WS connectivity */
  pingElevator: async (grantToken, buildingId, scopeToken = '', groupId = '1', region = 'global') => {
    const response = await fetch(`${API_BASE_URL}/ping`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ scopeToken, buildingId, groupId, region }),
    });
    if (!response.ok) throw new Error(await parseError(response, `Ping failed (${response.status})`));
    return response.json();
  },

  /** Make an elevator call via the backend (server-side WebSocket to KONE) */
  callElevator: async (scopeToken, buildingId, groupId, callType,
                       sourceFloor, destinationFloor,
                       dcsAction, landingAction, carAction, functionAction,
                       liftId, terminalId, ken = '', extraFields = {}) => {
    const body = {
      scopeToken, buildingId, groupId, callType,
      sourceFloor, destinationFloor,
      dcsAction, landingAction, carAction, functionAction,
      liftId, terminalId,
      ...extraFields,
    };
    if (ken) body.ken = ken;
    const response = await fetch(`${API_BASE_URL}/call`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await parseError(response, `Call elevator failed (${response.status})`));
    return response.json();
  },

  /** Hold car door open — must be called every 5 seconds to keep door open */
  holdOpen: async (scopeToken, buildingId, groupId, liftDeck, servedArea, hardTime = 5, softTime = 10, region = 'global') => {
    const response = await fetch(`${API_BASE_URL}/hold-open`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ scopeToken, buildingId, groupId, liftDeck, servedArea, hardTime, softTime, region }),
    });
    if (!response.ok) throw new Error(await parseError(response, `Hold open failed (${response.status})`));
    return response.json();
  },

  /** Resolve KEN to building ID via Device Mapping API */
  deviceMapping: async (kenId, token, region = 'global') => {
    const response = await fetch(`${API_BASE_URL}/device-mapping`, {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ kenId, grantToken: token, region }),
    });
    if (!response.ok) throw new Error(await parseError(response, `Device mapping failed (${response.status})`));
    return response.json();
  },

  /**
   * Subscribe to lift-deck-position events via SSE.
   * Returns an EventSource. Caller must close it when done.
   * onEvent(type, data) is called for each event.
   */
  trackLift: (scopeToken, buildingId, groupId, liftId, onEvent, maxEvents = 60, sessionId = '') => {
    const params = new URLSearchParams({
      scopeToken, buildingId, groupId: groupId || '1',
      liftId: liftId || '1', maxEvents,
      ...(sessionId ? { sessionId } : {}),
    });
    const url = `${API_BASE_URL}/track?${params.toString()}`;
    const es  = new EventSource(url);
    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        onEvent(msg.type, msg);
      } catch (_) {}
    };
    es.onerror = () => {
      onEvent('error', { type: 'error', error: 'SSE connection error' });
      es.close();
    };
    return es;
  },
};
